import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Settings2, Sparkles } from 'lucide-react';

export type ColumnType = 'name' | 'address' | 'other';

export interface ColumnMapping {
  columnName: string;
  type: ColumnType;
  autoDetected: boolean;
}

interface ColumnMapperProps {
  columns: string[];
  mappings: ColumnMapping[];
  onMappingChange: (columnName: string, type: ColumnType) => void;
  onApply: () => void;
  onCancel: () => void;
}

export const ColumnMapper = ({
  columns,
  mappings,
  onMappingChange,
  onApply,
  onCancel,
}: ColumnMapperProps) => {
  const getColumnMapping = (columnName: string): ColumnMapping => {
    return mappings.find(m => m.columnName === columnName) || {
      columnName,
      type: 'other',
      autoDetected: false,
    };
  };

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings2 className="h-5 w-5" />
          Column Mapping
        </CardTitle>
        <CardDescription>
          Specify which columns contain names or addresses. Auto-detected columns are marked with a sparkle.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4">
          {columns.map((columnName) => {
            const mapping = getColumnMapping(columnName);
            return (
              <div
                key={columnName}
                className="flex items-center gap-4 p-3 border rounded-lg bg-muted/30"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Label className="font-mono text-sm truncate">
                      {columnName}
                    </Label>
                    {mapping.autoDetected && (
                      <Badge variant="secondary" className="gap-1 text-xs">
                        <Sparkles className="h-3 w-3" />
                        Auto
                      </Badge>
                    )}
                  </div>
                </div>
                <Select
                  value={mapping.type}
                  onValueChange={(value) => onMappingChange(columnName, value as ColumnType)}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">
                      Name (parse to proper case)
                    </SelectItem>
                    <SelectItem value="address">
                      Address (split into parts)
                    </SelectItem>
                    <SelectItem value="other">
                      Keep as-is
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            );
          })}
        </div>

        <div className="flex gap-2 pt-4 border-t">
          <Button onClick={onApply} className="flex-1">
            Apply Mapping & Preview
          </Button>
          <Button onClick={onCancel} variant="outline">
            Cancel
          </Button>
        </div>

        <div className="text-xs text-muted-foreground space-y-1">
          <p><strong>Column Types:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Name:</strong> Converts "LAST FIRST" → "First Last" in proper case</li>
            <li><strong>Address:</strong> Splits into Street, City, State (abbreviated), ZIP</li>
            <li><strong>Keep as-is:</strong> No processing, keeps original value</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
